import { CompanyAttributes } from '@/types'
import { UpdateCompanyMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditCompanyQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { Heading } from '@/components/base/heading'
import { Divider } from '@/components/base/divider'

type Company = EditCompanyQuery['companies']['nodes'][0]

interface Props {
  id: string
}

export const Form = ({ id }: Props) => {
  const {
    save,
    saveSuccess,
    formBuilder,
    queryLoading,
    mutationLoading
  } = useForm<UpdateCompanyMutation, CompanyAttributes, EditCompanyQuery, Company>({
    id,
    initializer: company => ({
      name: company.name,
      address1: company.address1 || '',
      address2: company.address2 || '',
      address3: company.address3 || '',
      city: company.city || '',
      county: company.county || '',
      postcode: company.postcode || '',
      vatNumber: company.vatNumber || '',
      companyNumber: company.companyNumber || '',
      phoneNumber: company.phoneNumber || '',
      domain: company.domain || '',
      email: company.email || '',
      locale: company.locale || '',
      showPricesOnWebsite: company.showPricesOnWebsite || true,
    }),
    query: EditQuery,
    queryKey: 'companies',
    mutation: UpdateMutation,
    resultKey: 'updateCompany',
  })

  if (queryLoading) return <Loading />

  return (
    <div className="flex flex-col gap-6">
      { formBuilder.buildQueryError() }
      <div className="flex flex-col gap-6">
        <Heading>Company details</Heading>
        { formBuilder.buildField({ name: 'Name', field: 'name', type: 'text' }) }
        <div className="grid grid-cols-2 gap-6 w-full">
          { formBuilder.buildField({ name: 'Email', field: 'email', type: 'text' }) }
          { formBuilder.buildField({ name: 'Phone number', field: 'phoneNumber', type: 'text' }) }
        </div>
        <div className="grid grid-cols-2 gap-6 w-full">
          { formBuilder.buildField({ name: 'Company number', field: 'companyNumber', type: 'text' }) }
          { formBuilder.buildField({ name: 'VAT number', field: 'vatNumber', type: 'text' }) }
        </div>
        <div className="grid grid-cols-2 gap-6 w-full">
          { formBuilder.buildField({ name: 'Website domain', field: 'domain', type: 'text' }) }
          { formBuilder.buildField({ name: 'Show prices on website?', field: 'showPricesOnWebsite', type: 'switch' }) }
        </div>
      </div>
      <Divider />
      <div className="flex flex-col gap-6">
        <Heading>Address details</Heading>
        <div className="grid grid-cols-3 gap-6 w-full">
          { formBuilder.buildField({ name: 'Address line 1', field: 'address1', type: 'text' }) }
          { formBuilder.buildField({ name: 'Address line 2', field: 'address2', type: 'text' }) }
          { formBuilder.buildField({ name: 'Address line 3', field: 'address3', type: 'text' }) }
        </div>
        <div className="grid grid-cols-4 gap-6 w-full">
          { formBuilder.buildField({ name: 'City', field: 'city', type: 'text' }) }
          { formBuilder.buildField({ name: 'County', field: 'county', type: 'text' }) }
          { formBuilder.buildField({ name: 'Postcode', field: 'postcode', type: 'text' }) }
          { formBuilder.buildField({
            name: 'Country',
            field: 'locale',
            type: 'select',
            options: [{ value: 'en', display: 'UK' }, { value: 'us', display: 'US' }]
          }) }
        </div>
      </div>
      <div className="flex flex-row justify-end">
        { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
      </div>
    </div>
  )
}
